import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Save as SaveIcon, Close as CancelIcon } from "@mui/icons-material";
import moment from "moment";
import "moment-timezone";
import { IMaskInput } from "react-imask";
import { GetTimeZoneFromAbbreviation } from "common/Constants";

// 24-hour HH:mm:ss mask — keeps the input locale-independent so what the user
// types is what handleSave parses. (HTML <input type="time"> displays 12-hour
// AM/PM in some locales and was causing values to round-trip wrong.)
const Time24HourMask = React.forwardRef<HTMLElement, any>(function Time24HourMask(props, ref) {
  const { onChange, name, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="00:00:00"
      lazy={false}
      autofix
      overwrite
      inputRef={ref}
      onAccept={(value: string) => onChange({ target: { name, value } })}
    />
  );
});

interface EventInlineEditProps {
  event: any; // null for insert
  eventTypes: any[];
  waitReasons: any[];
  pointsOfService: any[];
  timeZone: string;
  onSave: (eventData: any) => void;
  onCancel: () => void;
}

const EventInlineEdit = ({
  event,
  eventTypes,
  waitReasons,
  pointsOfService,
  timeZone,
  onSave,
  onCancel,
}: EventInlineEditProps) => {
  const tz = GetTimeZoneFromAbbreviation(timeZone);

  const [typeId, setTypeId] = useState(event?.typeId ?? "");
  const [comments, setComments] = useState(event?.comments ?? "");
  const [date, setDate] = useState(() => {
    if (!event?.time) return "";
    return moment.utc(event.time).tz(tz).format("YYYY-MM-DD");
  });
  const [time, setTime] = useState(() => {
    if (!event?.time) return "";
    return moment.utc(event.time).tz(tz).format("HH:mm:ss");
  });
  const [routeLocation, setRouteLocation] = useState(event?.routeLocation ?? "");
  const [odometer, setOdometer] = useState(event?.odometer ?? "");
  const [closingComments, setClosingComments] = useState(event?.closingComments ?? "");

  const handleSave = () => {
    let utcTime = event?.time ?? null;
    if (date && time) {
      utcTime = moment.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm:ss", tz).utc().toISOString();
    } else if (date) {
      utcTime = moment.tz(`${date} 12:00:00`, "YYYY-MM-DD HH:mm:ss", tz).utc().toISOString();
    }

    onSave({
      ...event,
      typeId: typeId || null,
      comments: comments || null,
      time: utcTime,
      routeLocation: routeLocation || null,
      odometer: odometer ? Number(odometer) : null,
      closingComments: closingComments || null,
    });
  };

  return (
    <Box
      sx={{
        p: 1.5,
        border: 2,
        borderColor: "primary.main",
        borderRadius: "5px",
        backgroundColor: "#f5f9ff",
        mb: 2,
      }}
    >
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Event Type</InputLabel>
            <Select
              value={typeId}
              label="Event Type"
              onChange={(e) => setTypeId(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {eventTypes?.map((et) => (
                <MenuItem key={et.id} value={et.id}>
                  {et.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Wait/Cancel Reason</InputLabel>
            <Select
              value={comments}
              label="Wait/Cancel Reason"
              onChange={(e) => setComments(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {waitReasons?.map((r) => (
                <MenuItem key={r.name} value={r.name}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Date"
            type="date"
            size="small"
            fullWidth
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Time (24h)"
            size="small"
            fullWidth
            value={time}
            onChange={(e) => setTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputComponent: Time24HourMask as any }}
            placeholder="HH:MM:SS"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Route Location</InputLabel>
            <Select
              value={routeLocation}
              label="Route Location"
              onChange={(e) => setRouteLocation(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {pointsOfService?.map((l) => (
                <MenuItem key={l.id} value={l.id.toString()}>
                  {l.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Odometer"
            type="number"
            size="small"
            fullWidth
            value={odometer}
            onChange={(e) => setOdometer(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            label="Closing Comments"
            size="small"
            fullWidth
            value={closingComments}
            onChange={(e) => setClosingComments(e.target.value)}
          />
        </Grid>
      </Grid>
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
        <Button size="small" variant="contained" color="error" startIcon={<CancelIcon />} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
        >
          Save
        </Button>
      </Stack>
    </Box>
  );
};

export default EventInlineEdit;
